export const SELECTED_COMPANY_ID_STORAGE_KEY = "selectedCompanyId";
export const ACTIVE_COMPANY_TAB_STORAGE_KEY = "activeCompanyTab";
export const COMPANY_WIDGETS_DASHBOARD_STORAGE_KEY = "companyWidgetsDashboard";
export const COMPANY_EVENTS_PILL_STORAGE_KEY = "companyEventsPillKey";
export const COMPANY_CASHFLOW_WIDGET_HEIGHT_STORAGE_KEY = "companyCashflowWidgetHeightByCompany";

type ClientWorkspaceState = {
    companyId: string | null;
    activeTab: string | null;
    showWidgetsDashboard: boolean | null;
    eventsPillKey: string | null;
};

type ClientWorkspacePatch = {
    companyId?: string | null;
    activeTab?: string | null;
    showWidgetsDashboard?: boolean | null;
    eventsPillKey?: string | null;
};

type BooleanByCompanyStorage = Record<string, "0" | "1">;

const canUseLocalStorage = () => {
    if (typeof window === "undefined") return false;

    try {
        return !!window.localStorage;
    } catch {
        return false;
    }
};

const getItem = (key: string): string | null => {
    if (!canUseLocalStorage()) return null;

    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
};

const setItem = (key: string, value: string) => {
    if (!canUseLocalStorage()) return;

    try {
        window.localStorage.setItem(key, value);
    } catch {
    }
};

const removeItem = (key: string) => {
    if (!canUseLocalStorage()) return;

    try {
        window.localStorage.removeItem(key);
    } catch {
    }
};

const normalizeStorageCompanyId = (companyId: string | number | null | undefined) => {
    const normalizedCompanyId = String(companyId ?? "").trim();
    return normalizedCompanyId || null;
};

const getBooleanByCompanyStorage = (key: string): BooleanByCompanyStorage => {
    const raw = getItem(key);
    if (!raw) return {};

    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            return {};
        }

        return Object.entries(parsed).reduce<BooleanByCompanyStorage>((result, [companyId, value]) => {
            if (value === "0" || value === "1") {
                result[companyId] = value;
            }

            return result;
        }, {});
    } catch {
        return {};
    }
};

const setBooleanByCompanyStorage = (
    key: string,
    companyId: string | number | null | undefined,
    value: boolean | null,
) => {
    const normalizedCompanyId = normalizeStorageCompanyId(companyId);
    if (!normalizedCompanyId) return;

    const nextState = getBooleanByCompanyStorage(key);
    if (value === null) {
        delete nextState[normalizedCompanyId];
    } else {
        nextState[normalizedCompanyId] = value ? "1" : "0";
    }

    if (Object.keys(nextState).length === 0) {
        removeItem(key);
    } else {
        setItem(key, JSON.stringify(nextState));
    }
};

const getBooleanByCompanyValue = (
    key: string,
    companyId: string | number | null | undefined,
): boolean | null => {
    const normalizedCompanyId = normalizeStorageCompanyId(companyId);
    if (!normalizedCompanyId) return null;

    const value = getBooleanByCompanyStorage(key)[normalizedCompanyId];
    if (value === undefined) return null;

    return value === "1";
};

export const getStoredClientWorkspace = (): ClientWorkspaceState => {
    const widgetsValue = getItem(COMPANY_WIDGETS_DASHBOARD_STORAGE_KEY);

    return {
        companyId: getItem(SELECTED_COMPANY_ID_STORAGE_KEY),
        activeTab: getItem(ACTIVE_COMPANY_TAB_STORAGE_KEY),
        showWidgetsDashboard: widgetsValue === null ? null : widgetsValue === "1",
        eventsPillKey: getItem(COMPANY_EVENTS_PILL_STORAGE_KEY),
    };
};

export const saveClientWorkspaceState = (patch: ClientWorkspacePatch) => {
    if (patch.companyId !== undefined) {
        if (patch.companyId) {
            setItem(SELECTED_COMPANY_ID_STORAGE_KEY, patch.companyId);
        } else {
            removeItem(SELECTED_COMPANY_ID_STORAGE_KEY);
        }
    }

    if (patch.activeTab !== undefined) {
        if (patch.activeTab) {
            setItem(ACTIVE_COMPANY_TAB_STORAGE_KEY, patch.activeTab);
        } else {
            removeItem(ACTIVE_COMPANY_TAB_STORAGE_KEY);
        }
    }

    if (patch.showWidgetsDashboard !== undefined) {
        if (patch.showWidgetsDashboard === null) {
            removeItem(COMPANY_WIDGETS_DASHBOARD_STORAGE_KEY);
        } else {
            setItem(COMPANY_WIDGETS_DASHBOARD_STORAGE_KEY, patch.showWidgetsDashboard ? "1" : "0");
        }
    }

    if (patch.eventsPillKey !== undefined) {
        if (patch.eventsPillKey) {
            setItem(COMPANY_EVENTS_PILL_STORAGE_KEY, patch.eventsPillKey);
        } else {
            removeItem(COMPANY_EVENTS_PILL_STORAGE_KEY);
        }
    }
};

export const getStoredCompanyCashflowWidgetExpanded = (companyId: string | number | null | undefined) => (
    getBooleanByCompanyValue(COMPANY_CASHFLOW_WIDGET_HEIGHT_STORAGE_KEY, companyId)
);

export const saveCompanyCashflowWidgetExpanded = (
    companyId: string | number | null | undefined,
    isExpanded: boolean | null,
) => {
    setBooleanByCompanyStorage(COMPANY_CASHFLOW_WIDGET_HEIGHT_STORAGE_KEY, companyId, isExpanded);
};

export const clearClientWorkspaceState = () => {
    removeItem(SELECTED_COMPANY_ID_STORAGE_KEY);
    removeItem(ACTIVE_COMPANY_TAB_STORAGE_KEY);
    removeItem(COMPANY_WIDGETS_DASHBOARD_STORAGE_KEY);
    removeItem(COMPANY_EVENTS_PILL_STORAGE_KEY);
    removeItem(COMPANY_CASHFLOW_WIDGET_HEIGHT_STORAGE_KEY);
};
